import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import api from '../../utils/api';

interface Book {
  id: number;
  title: string;
  author: string;
  cover?: string;
  format: string;
  viewCount: number;
  uploadedAt: string;
  category?: string;
  categoryId?: number;
}

interface Category {
  id: number;
  name: string;
  slug: string;
}



const BookList: React.FC = () => {
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const isAdmin = user?.role === 'admin';
  const isAuthor = user?.role === 'author';
  const canUpload = isAdmin || isAuthor;

  const [books, setBooks] = useState<Book[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [bookTitle, setBookTitle] = useState('');
  const [bookAuthor, setBookAuthor] = useState('');
  const [bookCategory, setBookCategory] = useState('');
  
  // 分页状态
  const [currentPage, setCurrentPage] = useState(1);
  const [totalBooks, setTotalBooks] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const pageSizeOptions = [50, 100, 200, 500];

  // 分类列表：固定"全部" + API获取的分类
  const allCategories = [
    { id: 'all', name: '全部', slug: 'all' },
    ...categories
  ];

  useEffect(() => {
    fetchBooks();
    fetchCategories();
  }, []);

  // 监听 pageSize 变化时重新获取第一页
  useEffect(() => {
    // pageSize 变化时已经在 handlePageSizeChange 中处理了
  }, [pageSize]);


  const fetchCategories = async () => {
    try {
      const { data } = await api.get('/categories');
      setCategories(data);
    } catch (error) {
      console.error('获取分类失败:', error);
    }
  };

  const fetchBooks = async (keyword = '', category = 'all', page = 1) => {
    setLoading(true);
    try {
      let url = '/books';
      const params = [];
      if (keyword) params.push(`search=${encodeURIComponent(keyword)}`);
      if (category && category !== 'all') params.push(`category=${category}`);
      params.push(`page=${page}`);
      params.push(`limit=${pageSize}`);
      if (params.length > 0) url += '?' + params.join('&');
      
      const { data } = await api.get(url);
      setBooks(data.books || []);
      setTotalBooks(data.total || 0);
      setTotalPages(data.totalPages || 0);
      setCurrentPage(data.page || page);
    } catch (error) {
      console.error('获取图书列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchBooks(searchKeyword, selectedCategory);
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    fetchBooks(searchKeyword, category);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      const fileName = e.target.files[0].name.replace(/\.[^/.]+$/, '');
      setBookTitle(fileName);
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      setUploadProgress('请选择文件');
      return;
    }

    setUploading(true);
    setUploadProgress('上传中...');

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('title', bookTitle);
    formData.append('author', bookAuthor);
    formData.append('category', bookCategory);

    try {
      await api.post('/books/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setUploadProgress('上传成功！');
      setSelectedFile(null);
      setBookTitle('');
      setBookAuthor('');
      fetchBooks();
    } catch (error: any) {
      setUploadProgress(`上传失败: ${error.response?.data?.message || error.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {/* 标题和上传按钮 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2>书库</h2>
        <div>
          {canUpload && (
            <button 
              className="btn btn-primary"
              onClick={() => document.getElementById('upload-form')?.scrollIntoView({ behavior: 'smooth' })}
            >
              上传图书
            </button>
          )}
        </div>
      </div>

      {/* 分类筛选 */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {allCategories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => handleCategoryChange(String(cat.id))}
            className={String(selectedCategory) === String(cat.id) ? 'btn btn-primary' : 'btn btn-default'}
            style={{ 
              padding: '6px 16px', 
              fontSize: '14px',
              background: String(selectedCategory) === String(cat.id) ? '#1890ff' : 'transparent',
              color: String(selectedCategory) === String(cat.id) ? '#fff' : '#666',
              border: `1px solid ${String(selectedCategory) === String(cat.id) ? '#1890ff' : '#d9d9d9'}`,
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {/* 搜索框 */}
      <form onSubmit={handleSearch} style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
        <input
          type="text"
          className="form-input"
          placeholder="搜索图书..."
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          style={{ width: '240px' }}
        />
        <button type="submit" className="btn btn-primary">搜索</button>
        {searchKeyword && (
          <button 
            type="button" 
            className="btn btn-default"
            onClick={() => { setSearchKeyword(''); fetchBooks('', selectedCategory); }}
          >
            清除
          </button>
        )}
      </form>

      {/* 图书列表 */}
      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      ) : books.length > 0 ? (
        <div className="book-grid">
          {books.map((book) => (
            <Link to={`/book/${book.id}`} key={book.id} className="book-card">
              {book.cover ? (
                <img src={book.cover} alt={book.title} className="book-cover" />
              ) : (
                <div 
                  className="book-cover"
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: '#999',
                    fontSize: '14px',
                    background: '#f5f5f5'
                  }}
                >
                  暂无封面
                </div>
              )}
              <div className="book-info">
                <h3 className="book-title">{book.title}</h3>
                <p className="book-author">{book.author || '未知作者'}</p>
                <p style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                  {book.format.toUpperCase()} · {book.viewCount} 阅读
                </p>
                {book.categoryId && (
                  <span style={{
                    display: 'inline-block',
                    marginTop: '4px',
                    padding: '2px 8px',
                    background: '#e6f7ff',
                    color: '#1890ff',
                    fontSize: '12px',
                    borderRadius: '4px'
                  }}>
                    {allCategories.find(c => c.id === book.categoryId)?.name || '未分类'}
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#999' }}>
          <p>暂无图书</p>
          {!isAuthenticated && <p style={{ marginTop: '8px' }}>请先 <Link to="/login" style={{ color: '#1890ff' }}>登录</Link> 查看更多</p>}
        </div>
      )}

      {/* 分页控件 */}
      {totalBooks > 0 && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginTop: '24px',
          padding: '16px',
          background: '#fafafa',
          borderRadius: '4px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ color: '#666' }}>每页</span>
            <select
              value={pageSize}
              onChange={(e) => {
                const newSize = parseInt(e.target.value, 10);
                setPageSize(newSize);
                fetchBooks(searchKeyword, selectedCategory, 1);
              }}
              className="form-input"
              style={{ width: '80px', padding: '4px 8px' }}
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <span style={{ color: '#666' }}>本</span>
            <span style={{ color: '#999', marginLeft: '8px' }}>
              共 {totalBooks} 本 / {totalPages} 页
            </span>
          </div>
          
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={() => fetchBooks(searchKeyword, selectedCategory, 1)}
              disabled={currentPage === 1}
              className="btn btn-default"
              style={{ padding: '4px 12px' }}
            >
              首页
            </button>
            <button
              onClick={() => fetchBooks(searchKeyword, selectedCategory, currentPage - 1)}
              disabled={currentPage === 1}
              className="btn btn-default"
              style={{ padding: '4px 12px' }}
            >
              上一页
            </button>
            <span style={{ padding: '0 12px', color: '#333' }}>
              第 {currentPage} / {totalPages} 页
            </span>
            <button
              onClick={() => fetchBooks(searchKeyword, selectedCategory, currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="btn btn-default"
              style={{ padding: '4px 12px' }}
            >
              下一页
            </button>
            <button
              onClick={() => fetchBooks(searchKeyword, selectedCategory, totalPages)}
              disabled={currentPage >= totalPages}
              className="btn btn-default"
              style={{ padding: '4px 12px' }}
            >
              末页
            </button>
          </div>
        </div>
      )}


      {/* 上传表单 - 仅管理员和作者可见 */}
      {canUpload && (
        <div id="upload-form" className="card" style={{ marginTop: '40px' }}>
          <h3 style={{ marginBottom: '16px' }}>上传图书 (管理员/作者)</h3>
          <form onSubmit={handleUpload}>
            <div className="form-group">
              <label className="form-label">选择文件</label>
              <input
                type="file"
                accept=".epub,.pdf,.mobi,.azw3,.txt,.fb2,.docx"
                onChange={handleFileChange}
                style={{ display: 'block' }}
              />
              <p style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                支持格式: EPUB, PDF, MOBI, AZW3, TXT, FB2, DOCX
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">书名</label>
              <input
                type="text"
                className="form-input"
                value={bookTitle}
                onChange={(e) => setBookTitle(e.target.value)}
                placeholder="请输入书名"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">作者</label>
              <input
                type="text"
                className="form-input"
                value={bookAuthor}
                onChange={(e) => setBookAuthor(e.target.value)}
                placeholder="请输入作者"
              />
            </div>

            <div className="form-group">
              <label className="form-label">分类</label>
              <select
                className="form-input"
                value={bookCategory}
                onChange={(e) => setBookCategory(e.target.value)}
              >
                {allCategories.filter(c => String(c.id) !== 'all').map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <button type="submit" className="btn btn-primary" disabled={uploading}>
              {uploading ? '上传中...' : '上传'}
            </button>

            {uploadProgress && (
              <p style={{ 
                marginTop: '12px', 
                color: uploadProgress.includes('失败') ? '#ff4d4f' : '#52c41a' 
              }}>
                {uploadProgress}
              </p>
            )}
          </form>
        </div>
      )}
    </div>
  );
};

export default BookList;
